/**
 * Knowing when the running tab is out of date.
 *
 * The app is a single bundle with a content hash in its name, and a deploy
 * removes the previous build's files. An open tab keeps running the code it
 * loaded — nothing tells it a newer build exists — so people can sit on
 * week-old code and never see a fix that shipped hours ago. Twice in one day
 * that looked like "the feature was not deployed".
 *
 * So: read the bundle name out of the shell the server hands out right now and
 * compare it with the one this tab is running.
 */
const BUNDLE_RE = /assets\/(index-[A-Za-z0-9_-]+\.js)/

/** The bundle named by a freshly fetched index.html, e.g. `index-Bh5Rlo8Y.js`. */
export function parseBundleFromHtml(html: string): string | null {
  return BUNDLE_RE.exec(html)?.[1] ?? null
}

/**
 * Only a comparison of two known names means anything. In dev there is no
 * hashed bundle, and a failed fetch tells us nothing — neither may nag.
 */
export function isUpdateAvailable(current: string | null, deployed: string | null): boolean {
  return current != null && deployed != null && current !== deployed
}

/** Reads the shell the server would serve a new visitor. Never throws. */
export async function fetchDeployedBundle(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    // `cache: 'no-store'` so the answer is the deployed shell, not the one this
    // tab already has.
    const res = await fetchImpl('/', { cache: 'no-store' })
    if (!res.ok) return null
    return parseBundleFromHtml(await res.text())
  } catch {
    return null
  }
}
